import { cx } from '@linaria/core';
import { useRef, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useNotifier } from '@provider/notifier';
import { Tooltip } from '@ui/kit';
import { PauseIcon, PlayIcon, CrossIcon } from '@ui/kit/icons';
import { useAudio, useSelf, useStateRef, useLoaderState, CSSRefTransition } from '@utils/react';
import { useAutoPauseOnViewportExit } from './hooks/use-pause-on-viewport-exit.js';
import { containerCls, toggleIconCls, TOGGLE_ICON_TRANSITION_MS, toggleThrobberCls, toggleCls, progressbarCls, progressLineCls, progressHoverTransitionCls, PROGRESS_HOVER_TRANSITION_MS, progressHoverLabelCls, timestampCls, timestampSeparatorCls, closeButtonCls } from './molecule.audio-player.styles.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function AudioPlayer(props) {
  const notifier = useNotifier();
  const containerRef = useRef(null);
  const progressbarRef = useRef(null);
  const handleCloseClick = () => {
    audio.pause();
    props.onEnd?.();
  };
  const onError = () => {
    notifier.notify({
      id: "audio.play.error",
      body: "Der opstod en fejl ved afspilningen af lydfilen.",
      type: "error"
    });
  };
  const audio = useAudio({
    ...props,
    autoplay: true,
    notifier,
    onError
  });
  const self = useSelf({});
  const [hoverAtMs, setHoverAtMs] = useState();
  const [scrubToMs, setScrubToMs] = useStateRef(undefined);
  const showLoader = useLoaderState(audio.isLoading, {
    durationMs: 350
  });
  const handleToggleClick = () => {
    if (audio.isPlaying) {
      audio.pause();
    } else {
      audio.resume();
    }
  };
  const handleProgressPointerDown = evt => {
    if (!evt.isPrimary || evt.button !== 0) {
      return;
    }
    evt.preventDefault();
    self.isScrubbing = true;
    self.wasPlayingBeforeScrubbing = audio.isPlaying;
    handleProgressPointerMove(evt, {
      avoidPause: true
    });
    window.addEventListener("pointermove", handleProgressPointerMove);
    window.addEventListener("pointerup", handleProgressPointerUp);
    window.addEventListener("pointercancel", handleProgressPointerUp);
  };
  const handleProgressPointerUp = () => {
    window.removeEventListener("pointermove", handleProgressPointerMove);
    window.removeEventListener("pointerup", handleProgressPointerUp);
    window.removeEventListener("pointercancel", handleProgressPointerUp);
    if (scrubToMs.current !== undefined) {
      audio.seek(scrubToMs.current);
    }
    if (self.wasPlayingBeforeScrubbing) {
      audio.resume();
    }
    self.isScrubbing = false;
    setScrubToMs(undefined);
  };
  const handleProgressPointerMove = (evt, options = {}) => {
    evt.preventDefault();
    if (!audio.durationMs) {
      return;
    }
    const bbox = progressbarRef.current?.getBoundingClientRect();
    if (!bbox) {
      return;
    }
    const rawPositionMs = (evt.pageX - bbox.left) / bbox.width * audio.durationMs;
    const positionMs = Math.max(0, Math.min(audio.durationMs, rawPositionMs));

    // We should only update the hover position if we're actually still hovering
    // within the progressbar range
    if (rawPositionMs >= 0 && rawPositionMs <= audio.durationMs) {
      setHoverAtMs(positionMs);
    }
    if (!self.isScrubbing) {
      return;
    }
    setScrubToMs(positionMs);
    if (!options.avoidPause) {
      audio.pause();
    }
  };
  const handleProgressPointerLeave = () => {
    setHoverAtMs(undefined);
  };
  const elapsedTimeMs = scrubToMs.current ?? audio.timeElapsedMs;
  const isPlaying = self.isScrubbing ? self.wasPlayingBeforeScrubbing ?? false : audio.isPlaying;
  useAutoPauseOnViewportExit(containerRef, audio.isPlaying, audio.pause);
  return /*#__PURE__*/jsxs("div", {
    className: containerCls,
    ref: containerRef,
    children: [/*#__PURE__*/jsx(Tooltip, {
      placement: "bottom",
      body: isPlaying ? "Pause" : "Afspil",
      ariaType: "label",
      disabled: showLoader.current,
      children: ref => /*#__PURE__*/jsxs("button", {
        ref: ref,
        className: cx(toggleCls, showLoader.current && "hasThrobber"),
        onClick: handleToggleClick,
        children: [/*#__PURE__*/jsx(CSSRefTransition, {
          in: showLoader.current,
          timeout: TOGGLE_ICON_TRANSITION_MS,
          classNames: toggleIconCls,
          mountOnEnter: true,
          unmountOnExit: true,
          children: ref => /*#__PURE__*/jsx("div", {
            ref: ref,
            className: toggleThrobberCls
          })
        }), /*#__PURE__*/jsx(CSSRefTransition, {
          in: !showLoader.current && isPlaying,
          timeout: TOGGLE_ICON_TRANSITION_MS,
          classNames: toggleIconCls,
          mountOnEnter: true,
          unmountOnExit: true,
          children: ref => /*#__PURE__*/jsx(PauseIcon, {
            ref: ref,
            className: toggleIconCls,
            height: 20
          })
        }), /*#__PURE__*/jsx(CSSRefTransition, {
          in: !showLoader.current && !isPlaying,
          timeout: TOGGLE_ICON_TRANSITION_MS,
          mountOnEnter: true,
          unmountOnExit: true,
          children: ref => /*#__PURE__*/jsx(PlayIcon, {
            ref: ref,
            className: toggleIconCls,
            height: 18,
            style: {
              marginLeft: "1px"
            },
            fill: "none",
            color: "currentColor"
          })
        })]
      })
    }), /*#__PURE__*/jsxs("div", {
      ref: progressbarRef,
      className: progressbarCls,
      onPointerDown: handleProgressPointerDown,
      onPointerMove: handleProgressPointerMove,
      onPointerLeave: handleProgressPointerLeave,
      children: [/*#__PURE__*/jsx("div", {
        className: progressLineCls,
        style: {
          width: `${(elapsedTimeMs ?? 0) / (audio.durationMs ?? 1) * 100}%`
        }
      }), /*#__PURE__*/jsx(TransitionGroup, {
        component: null,
        children: hoverAtMs && /*#__PURE__*/jsx(CSSRefTransition, {
          timeout: PROGRESS_HOVER_TRANSITION_MS,
          classNames: progressHoverTransitionCls,
          children: ref => /*#__PURE__*/jsx("div", {
            ref: ref,
            role: "presentation",
            className: cx(progressLineCls, "hoverLine"),
            style: {
              width: `${(hoverAtMs ?? 0) / (audio.durationMs ?? 1) * 100}%`
            },
            children: /*#__PURE__*/jsx("span", {
              className: progressHoverLabelCls,
              children: formatTime(hoverAtMs)
            })
          })
        }, "hoverline")
      })]
    }), /*#__PURE__*/jsxs("span", {
      className: timestampCls,
      children: [formatTime(elapsedTimeMs), /*#__PURE__*/jsx("span", {
        className: timestampSeparatorCls
      }), formatTime(audio.durationMs)]
    }), /*#__PURE__*/jsx(Tooltip, {
      placement: "bottom",
      body: "Stop afspilning",
      ariaType: "label",
      children: ref => /*#__PURE__*/jsx("button", {
        ref: ref,
        onClick: handleCloseClick,
        className: closeButtonCls,
        children: /*#__PURE__*/jsx(CrossIcon, {
          height: 18
        })
      })
    })]
  });
}

/**
 * transform a time given in milliseconds into a human readable mm:ss format
 */
function formatTime(timeMs) {
  if (timeMs === undefined) {
    return "--:--";
  }
  const timeSeconds = Math.round(timeMs / 1000);
  const formatedMinutes = String(Math.floor(timeSeconds / 60)).padStart(2, "0");
  const formatedSeconds = String(timeSeconds % 60).padStart(2, "0");
  return `${formatedMinutes}:${formatedSeconds}`;
}

export { AudioPlayer };
//# sourceMappingURL=molecule.audio-player.js.map
